import React, { useEffect, useRef, useState } from "react";
import Select, {
  ActionMeta,
  ControlProps,
  MultiValue,
  SingleValue,
  components,
} from "react-select";
import { useDebounce } from "../../utils";
import { httpCall } from "../../utils";
import {
  sortOptionsByBasicMatch,
  sortOptionsByFuzzyMatch,
} from "@/utils/mediautils/CommonFunctions";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export interface Option {
  label: string;
  value: number | string | string[] | Option[] | number[] | boolean;
  customLabel?: React.ReactNode;
  isDisabled?: boolean;
}

interface axiosResponse {
  result: any;
  code: number;
  erorr: string;
}

const deduplicateOptions = (options: Option[]): Option[] => {
  const seen = new Map();
  for (const opt of options) {
    if (!seen.has(opt.value)) {
      seen.set(opt.value, opt);
    }
  }
  return Array.from(seen.values());
};

interface SelectProps {
  options?: Option[];
  name?: string;
  handleChange?: (
    selectedOption: SingleValue<Option> | MultiValue<Option>,
    actionMeta: ActionMeta<Option>
  ) => void;
  value: number | string | string[] | Option[] | number[]; // Allow either a string for single select or string[] for multi-select
  placeholder?: React.ReactNode | string;
  label?: string;
  disabled?: boolean;
  className?: string;
  multiSelect?: boolean;
  handleClear?: any;
  showRequired?: boolean;
  error?: any;
  touched?: boolean;
  handleBlur?: (e: any) => void;
  showHeader?: boolean;
  hostSubSection?: boolean;
  hideArrow?: boolean;
  customOptionRenderer?: (option: Option) => React.ReactNode;
  updatedOptions?: Option[];
  menuAlign?: "left" | "right" | "auto" | "extend-right";
  customStyling?: React.CSSProperties;
  maintainSelectHeight?: string;
  // for suggestions
  apiUrl?: string;
  apiRequestBody?: Record<any, any>;
  rpcCallMethod?: string;
  apiCallMethod?: HttpMethod;
  apiResponseFormatter?: any;
  rpcCallParams?: Record<any, any>;
  groupPosition?: "left" | "right";

  //for making API call when input changes
  onInputChangeCallback?: (params: {
    hostid?: string;
    keyword: string;
  }) => void;
  callbackParams?: any;
  onDebouncedInputChange?: (inputValue: string) => void;
  creditData?: any;
  checkBox?: boolean;
  menuWidth?: string;
  isMenuWidthFixed?: boolean;
  customValueRenderer?: (option: Option) => React.ReactNode;
  ishost?: boolean;
  loader?: boolean;
  noOptionsMessage?: any;
  isNoOptionsMessage?: boolean;

  // New props for label checkbox
  labelCheckbox?: boolean;
  labelCheckboxChecked?: boolean;
  onLabelCheckboxChange?: (checked: boolean) => void;
  isPreview?: boolean;
  showAsterisk?: boolean;
  // when provided, replaces default yellow bullet with this dot class
  asteriskClassName?: string;
  disableLabelCheckbox?: boolean;
  readOnlyMode?: boolean;
  lowOpacity?: boolean;
  isSearchable?: boolean;
  disableMenuPortal?: boolean;
  clearOnSetValue?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  sortMode?: "basic" | "fuzzy";
  /** Keep the menu open after picking an option (multi-add flows). Defaults to !checkBox. */
  closeMenuOnSelect?: boolean;
}

export const CustomSelect: React.FC<SelectProps> = ({
  disabled,
  name,
  options = [],
  label,
  value,
  groupPosition,
  handleChange,
  placeholder = "Select an option",
  className,
  multiSelect = false,
  handleClear,
  showRequired,
  touched,
  error,
  handleBlur,
  showHeader,
  apiUrl,
  apiCallMethod = "POST",
  apiRequestBody,
  hostSubSection,
  rpcCallMethod,
  onInputChangeCallback,
  callbackParams,
  hideArrow = false,
  customOptionRenderer,
  updatedOptions = [],
  onDebouncedInputChange,
  creditData = [],
  checkBox = false,
  menuWidth,
  isMenuWidthFixed = false,
  menuAlign = "auto",
  customValueRenderer,
  customStyling,
  ishost,
  maintainSelectHeight,
  loader,
  noOptionsMessage,
  isNoOptionsMessage,
  labelCheckbox = false,
  labelCheckboxChecked = false,
  onLabelCheckboxChange,
  isPreview = false,
  showAsterisk = false,
  asteriskClassName,
  disableLabelCheckbox = false,
  readOnlyMode = false,
  lowOpacity = true,
  isSearchable = true,
  disableMenuPortal = false,
  clearOnSetValue = false,
  onMenuOpen,
  onMenuClose,
  sortMode = "basic",
  closeMenuOnSelect,
}) => {
  const [suggestedOptions, setSuggestedOptions] = useState<Option[]>([]);
  const suggestedOptionsRef = useRef<Option[]>([]);
  const [inputText, setInputText] = useState<string>("");
  const [inputVersion, setInputVersion] = useState(0);

  const debouncedInputText = useDebounce(inputText, 200);
  const debouncedInputVersion = useDebounce(inputVersion, 200);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const [singleselectedOptions, setSingleSelectedOptions] =
    useState<any>(value);
  const [multiselectedOptions, setMultiSelectedOptions] = useState<any>([]);

  if (multiSelect) {
    options = deduplicateOptions([...options, ...multiselectedOptions]);
  }

  // Fire external input-change callback ONLY when the user types (debounced).
  useEffect(() => {
    if (!onInputChangeCallback) return;
    if (debouncedInputText.length === 0) return;
    setIsLoading(true);
    if (callbackParams) {
      onInputChangeCallback({
        keyword: debouncedInputText,
        hostid: callbackParams,
      });
    } else {
      onInputChangeCallback({ keyword: debouncedInputText });
    }
    setIsLoading(false);
  }, [debouncedInputText, debouncedInputVersion]);

  // API suggestions path (when apiUrl is provided)
  useEffect(() => {
    if (!(apiUrl && inputText)) return;
    setIsLoading(true);
    (async () => {
      try {
        const data = await httpCall<axiosResponse>({
          url: apiUrl,
          method: apiCallMethod,
          data: {
            ...(rpcCallMethod && { method: rpcCallMethod }),
            ...(rpcCallMethod && { params: { title: inputText } }),
            ...(!rpcCallMethod && { value: inputText }),
          },
        });
        if (data.code === 200) {
          const fetchedOptions = data.result.list.map((media: any) => ({
            label: media?.title,
            value: media?.id,
          }));
          const previousSuggestedOptions = multiSelect
            ? suggestedOptionsRef.current.filter(
                (option) =>
                  Array.isArray(value) &&
                  (value as (string | number)[])?.includes(
                    option.value as string | number
                  )
              )
            : suggestedOptionsRef.current.filter(
                (option) => option.value === value
              );

          suggestedOptionsRef.current = deduplicateOptions([
            ...fetchedOptions,
            ...previousSuggestedOptions,
          ]);
          setSuggestedOptions(suggestedOptionsRef.current);
        }
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [apiUrl, inputText, apiCallMethod, rpcCallMethod, multiSelect, value]);

  const o = apiUrl
    ? suggestedOptions.length > 0
      ? suggestedOptions
      : options
    : updatedOptions && updatedOptions.length > 0
      ? updatedOptions
      : options;

  const sortedO = inputText
    ? sortMode === "fuzzy"
      ? sortOptionsByFuzzyMatch(o, inputText)
      : sortOptionsByBasicMatch(o, inputText)
    : o;
  useEffect(() => {
    if (loader === false) return;
    if (inputText) {
      const hasMatchingOption = o?.some((option) =>
        option?.label?.toLowerCase().includes(inputText.toLowerCase())
      );
      if (!hasMatchingOption) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
        setIsLoadingComplete(true);
      }
    }
  }, [inputText, o, loader]);

  const allOptions: Option[] = deduplicateOptions([
    ...options,
    ...suggestedOptions,
    ...creditData,
    ...updatedOptions,
  ]);

  const valueInOptionFormat = multiSelect
    ? allOptions?.filter(
        (option) =>
          Array.isArray(value) &&
          (value as (string | number)[])?.includes(
            option?.value as string | number
          )
      )
    : allOptions?.find((option) => option?.value === value) || null;

  // ---- to display the search icon in the starting
  const CustomControl = (props: ControlProps<any>) => (
    <components.Control {...props}>
      <i className="bi bi-search ms-2 iconSvg"></i>
      {props.children}
    </components.Control>
  );

  const CustomOption = (props: any) => {
    const { data, isSelected, selectOption, isFocused } = props;

    return (
      <components.Option {...props}>
        <div
          className="d-flex align-items-center"
          style={{
            // backgroundColor: isFocused ? '#eee' : 'transparent',
            color: isFocused ? "#0d6efd" : "inherit",
            // height: isFocused ? '25px' : '',
          }}
        >
          {checkBox && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => selectOption(data)}
              className="me-2"
              style={{ cursor: "pointer" }}
            />
          )}

          {customOptionRenderer
            ? customOptionRenderer(data)
            : data.customLabel || data.label}
        </div>
      </components.Option>
    );
  };

  // Add a custom SingleValue component
  const CustomSingleValue = (props: any) => {
    if (customValueRenderer) {
      return (
        <components.SingleValue {...props}>
          {customValueRenderer(props.data)}
        </components.SingleValue>
      );
    }
    return <components.SingleValue {...props} />;
  };

  const customStyles = {
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? "white" : provided.backgroundColor,
      color: state.isSelected ? "#818181" : provided.color,
      "&:hover": {
        backgroundColor: "#f0f0f0",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "var(--bs-primary-color)",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#999",
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: "#fff",
      ...(customStyling || {}),
      ...(customStyling?.borderColor && {
        "&:hover": { borderColor: customStyling.borderColor },
      }),
      ...((disabled || state?.isDisabled) && { opacity: lowOpacity ? 0.5 : 1 }),
    }),

    indicatorSeparator: (base: any) => ({
      ...base,
      display: "none",
    }),
    input: (provided: any) => ({
      ...provided,
      gridTemplateColumns: "0 1fr",
    }),
    // to uncomment
    ...(ishost && {
      indicatorsContainer: (provided: any) => ({
        ...provided,
        padding: "0",
      }),
      input: (provided: any) => ({
        ...provided,
        caretColor: "transparent",
      }),
      dropdownIndicator: (provided: any) => ({
        ...provided,
        padding: "0",
        ...(!hideArrow
          ? {
              svg: {
                height: "10px",
                width: "10px",
              },
            }
          : { display: "none" }),
      }),
    }),

    // dropdownIndicator: (provided: any) => ({
    //   ...provided,
    //   padding: '0',
    //   ...(ishost &&
    //     !hideArrow && {
    //       svg: {
    //         height: '10px',
    //         width: '10px',
    //       },
    //     }),
    // }),
  };

  return (
    <div className={`d-flex flex-column mb-0 ${className}`}>
      {showHeader !== false && (
        <div className="d-flex align-items-center gap-1">
          <label>
            {labelCheckbox && !readOnlyMode && (
              <input
                type="checkbox"
                checked={labelCheckboxChecked}
                onChange={(e) => onLabelCheckboxChange?.(e.target.checked)}
                className="me-2"
                style={{ cursor: "pointer" }}
                disabled={disableLabelCheckbox}
              />
            )}
            {label}
            {showRequired && <span className="required ms-1">*</span>}
            {showAsterisk &&
              (asteriskClassName ? (
                <span
                  className={` ${asteriskClassName}`}
                  style={{ marginLeft: "5px", fontSize: "20px" }}
                />
              ) : (
                <span
                  className="align-middle text-warning"
                  style={{ fontSize: "35px" }}
                >
                  &#8226;
                </span>
              ))}
          </label>
        </div>
      )}
      <div className="position-relative">
        <Select
          {...(apiUrl && {
            components: {
              Control: CustomControl,
            },
          })}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue,
          }}
          menuPortalTarget={disableMenuPortal ? undefined : document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),

            valueContainer: (base) => ({
              ...base,
              //flexWrap: 'nowrap',
              overflowY: "auto",
              minHeight: maintainSelectHeight ? "" : "40px",
              maxHeight: "80px",
              "&::-webkit-scrollbar": {
                height: "2px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
            }),
            ...(isMenuWidthFixed && {
              menu: (base) => ({
                ...base,
                width: menuWidth,
                ...(menuAlign === "right" && {
                  right: 0,
                  left: "auto",
                }),
                ...(menuAlign === "left" && {
                  left: 0,
                  right: "auto",
                }),
                ...(menuAlign === "extend-right" && {
                  right: 0,
                  left: "auto",
                  minWidth: "max-content",
                  maxWidth: "100%",
                }),
              }),
            }),
            ...(menuAlign === "extend-right" &&
              !isMenuWidthFixed && {
                menu: (base) => ({
                  ...base,
                  right: 0,
                  left: "auto",
                  minWidth: "max-content",
                  maxWidth: "100%",
                }),
              }),
            multiValue: (base) => ({
              ...base,
              margin: "2px",
              flexShrink: 0,
              maxWidth: "110px",
              display: "flex",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              alignItems: "center",
              borderRadius: "0.45rem",
              "& > div": {
                maxWidth: "90px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              "& > svg": {
                marginLeft: "4px",
                flexShrink: 0,
              },
            }),
            dropdownIndicator: (base) => ({
              ...base,
              padding: maintainSelectHeight ? "0px 0px" : "",
            }),
            ...(hideArrow && {
              dropdownIndicator: (base) => ({
                ...base,
                display: "none",
              }),
            }),
            ...customStyles,
          }}
          name={name}
          isMulti={multiSelect}
          classNamePrefix="react-select"
          isOptionSelected={(option, selectValue) =>
            Array.isArray(selectValue) &&
            selectValue.some((selected) => selected.value === option.value)
          }
          isDisabled={disabled}
          isSearchable={isSearchable}
          closeMenuOnSelect={closeMenuOnSelect ?? !checkBox}
          options={sortedO}
          hideSelectedOptions={multiSelect && !checkBox}
          noOptionsMessage={
            isNoOptionsMessage
              ? noOptionsMessage
              : () => {
                  if (loader == false) return null;
                  if (!inputText) return null;
                  if (!isLoadingComplete) return null;
                  const hasMatchingOption = o?.some((option) =>
                    option?.label
                      ?.toLowerCase()
                      .includes(inputText.toLowerCase())
                  );
                  return hasMatchingOption ? null : "No Matches Found";
                }
          }
          menuPlacement="auto"
          menuPosition={disableMenuPortal ? "absolute" : "fixed"}
          menuShouldScrollIntoView={false}
          filterOption={onInputChangeCallback ? () => true : undefined}
          value={hostSubSection ? [] : valueInOptionFormat}
          {...(apiUrl && {
            onInputChange: (value) => {
              setInputText(value);
            },
          })}
          onInputChange={(value, action) => {
            if (action.action === "input-change") {
              setInputText(value);
              setInputVersion((v) => v + 1);
              setIsLoadingComplete(false);
              if (loader && value) {
                setIsLoading(true);
              } else {
                setIsLoading(false);
              }
            } else if (action.action === "set-value") {
              setInputText("");
              setIsLoading(false);
              setIsLoadingComplete(false);
              if (clearOnSetValue && onInputChangeCallback) {
                onInputChangeCallback({ keyword: "" });
              }
            }
            return value;
          }}
          onChange={(selectedOption, actionMeta) => {
            if (handleChange) {
              handleChange(selectedOption, actionMeta);
            }
            if (multiSelect && Array.isArray(selectedOption)) {
              setMultiSelectedOptions(selectedOption);
            }
          }}
          onBlur={handleBlur}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          backspaceRemovesValue
          onKeyDown={(e) => {
            if (!multiSelect && e.key === "Backspace" && !inputText) {
              e.preventDefault();
              handleChange?.({ label: "", value: "" }, {
                action: "clear",
                removedValues: [],
              } as unknown as ActionMeta<Option>);
            }
          }}
          placeholder={placeholder}
        />
        {loader && isLoading && inputText && !isLoadingComplete && (
          <div
            className="position-absolute bottom-0 start-0 w-100"
            style={{ height: "2px" }}
          >
            <div
              key={inputText}
              className="bg-primary"
              style={{
                width: "100%",
                height: "100%",
                animation: "progress 2s ease-in-out forwards",
                background: "#0d6efd",
                transformOrigin: "left",
                transform: "scaleX(0)",
              }}
              onAnimationEnd={() => {
                setIsLoadingComplete(true);
                setIsLoading(false);
              }}
            />
            <style>
              {`
                @keyframes progress {
                  0% {
                    transform: scaleX(0);
                  }
                  100% {
                    transform: scaleX(1);
                  }
                }
              `}
            </style>
          </div>
        )}
        {touched && error && (
          <div className="form-text text-danger px-2">{error}</div>
        )}
      </div>
    </div>
  );
};

export default CustomSelect;
